import {
  isPreCalibrationSubscription,
  isResolvedSubscription,
  isSubscriptionLapseConfirmed,
  type ResolvedSubscriptionState,
  type SubscriptionState,
} from "@/hooks/queries/useSubscriptionState";

// gh-568 — source-aware copy for lapse-disabled write CTAs. An ORG-SEAT
// clinician cannot self-renew: B2B renewal is manual and off-platform (CITS ↔
// the organization), which is why every 13.6 lapse surface (workspace banner
// helper, Profile subscription section, access-and-readiness card) points at
// the organization administrator. The self-serve "Renew the subscription…"
// wording is correct ONLY for the B2C variants (10.5's surface).

/** The B2B blocked-write reason — the 13.6 surfaces compose their copy FROM this
 *  constant (Profile subscription section, access-and-readiness card), so the
 *  sentence cannot drift between them. */
export const ORG_LAPSE_CONTACT_ADMIN =
  "Contact your organization administrator to renew.";

/**
 * The org-seat lapse discrimination — the ONE predicate every 13.6 surface keys
 * on (the tooltip helper below, the workspace readiness card's copy swap). Kept
 * here so call sites cannot restate-and-diverge it (PR-584 review).
 */
export function isOrgSeatLapsed(state: SubscriptionState): boolean {
  return state.status === "lapsed" && state.source === "org_seat";
}

/**
 * Pick the blocked-write tooltip for a lapsed state: org seat → the
 * contact-your-administrator copy; anything else (B2C lapse, bare pre-10.8
 * fixtures without resolved facts) → the caller's per-control self-serve copy.
 * Callers only render the result while their CTA is lapse-disabled — for
 * active/loading states the helper just echoes `selfServeCopy` and never
 * invents a block reason.
 */
export function lapsedWriteTooltip(
  state: SubscriptionState,
  selfServeCopy: string,
): string {
  return isOrgSeatLapsed(state) ? ORG_LAPSE_CONTACT_ADMIN : selfServeCopy;
}

// ===========================================================================
// Billing display-state consolidation — the ONE subscription-pill mapper.
//
// Closes the ledger entry "billing display-state consolidation — one shared
// subscription-state mapper for Profile + TopBar (+ workspace)" (deferred from
// the PR #578 review, 2026-07-24; this file is the home that entry named), and
// with it the two findings that were its symptoms:
//
//   - DW-437 — the same state read "Active" on the Profile card and
//     "Subscription active" in the TopBar, because each surface owned its own
//     copy table;
//   - DW-458 (16.7 review, HIGH) — `AccessReadinessCard` re-derived the
//     legacy-onboarding split WITHOUT the `certVerified` term its sibling in
//     `SubscriptionSection` carries, so the two renderers answered the same
//     question differently and a population read one card contradicting the
//     other on the same page.
//
// Before this, three surfaces asked "what does this subscription look like?"
// with three different discriminators:
//
//   TopBar chip     `status === "active"` → `isPreCalibrationSubscription`
//                   → `isSubscriptionLapseConfirmed`
//   Profile card    `isActive` → `isPreCalibrationNotes` → a LOCAL `isOrgLapsed`
//   workspace card  `isSubscriptionLapsed && !isPreCalibrationSubscription`
//
// They agreed on every input except the org-seat lapse, and nothing but
// hand-maintained lockstep comments held them together. Everything below is now
// the single source of BOTH the pill variant and its text; no surface may
// re-derive either, and `src/lib/__tests__/subscription-pill-single-source.test.tsx`
// fails if one starts to.
// ===========================================================================

/** Badge variants the subscription pill may take (see `components/ui/badge.tsx`). */
export type SubscriptionPillVariant = "ok" | "pending" | "warning" | "blocked";

/**
 * Story 16.7's two legacy-ONBOARDING moments — the states between "CITS
 * verified my certificate" and "my account is enabled". They sit outside the
 * billing states below because the billing payload cannot tell them apart on
 * its own: `entitlements._no_entitlement` returns `source="individual_subscription"`
 * for a clinician with no subscription row at all, so the JWT (`isLegacyGated`
 * + `certVerified`) is the outer gate and `notes` is what splits it.
 */
export type LegacyOnboardingState = "awaiting_payment" | "activating";

/** Every display state the pill can be in — the ONE discriminator. */
export type SubscriptionPillState =
  | LegacyOnboardingState
  | "active"
  | "pending_calibration"
  | "org_lapsed"
  | "inactive"
  | "lapsed";

export interface SubscriptionPillDescriptor {
  state: SubscriptionPillState;
  variant: SubscriptionPillVariant;
  text: string;
}

/**
 * Screen 14b's own moment — STAGE 1 of the legacy onboarding: the certificate
 * has been uploaded and a CITS admin has not yet approved it.
 *
 * It sits OUTSIDE `SubscriptionPillState`, and not for want of a table row.
 * Every state in that union answers "what does this ENTITLEMENT look like?", and
 * stage 1 is the moment before one can exist: FR39 admits
 * `POST /billing/checkout-sessions/create` only once `cert_verified` is true and
 * answers 403 LEGACY-001 to everybody else. There is no billing fact to describe
 * here and nothing for `isBillingBlockedPillState` to classify — this reader is
 * held by ACCOUNT activation, not by an entitlement.
 */
export type LegacyPendingVerificationState = "pending_verification";

/** Every state a rendered pill may carry — the table below, plus 14b's stage 1. */
export type RenderedPillState =
  | SubscriptionPillState
  | LegacyPendingVerificationState;

/**
 * What a surface actually renders. `SubscriptionPillDescriptor` is the
 * table-backed subset of this, and every value in `SUBSCRIPTION_PILL` is one.
 */
export interface RenderedPillDescriptor {
  state: RenderedPillState;
  variant: SubscriptionPillVariant;
  text: string;
}

// ---------------------------------------------------------------------------
// PRODUCT DECISION — PM ruling, 2026-08-31 (resolves DW-437).
//
// The active-state label is "Subscription active" on EVERY surface. Story 16.6
// had renamed the Profile card's active pill to a bare "Active" (UX §13.11);
// that half of the rename is REVERTED by this ruling. The PM's reason: in the
// top bar, a bare "Active" does not say WHAT is active — the word has to carry
// its own subject, because the chip sits alone in the header with no
// "Subscription" heading above it to lend one.
//
// The PENDING label is NOT touched by the ruling: 16.6's "Activating after
// calibration" stands (UX spec §13.11), and since this table is now the single
// source of the text, the TopBar chip follows the card here rather than keeping
// its old "Pending calibration".
// ---------------------------------------------------------------------------
export const SUBSCRIPTION_PILL: Record<
  SubscriptionPillState,
  SubscriptionPillDescriptor
> = {
  // Story 16.7's onboarding rows are all `pending`, and the text names what is
  // being waited on. "Subscription lapsed" — the generic answer for an inactive
  // B2C claim — would be a lie to a clinician who has never held a subscription.
  awaiting_payment: {
    state: "awaiting_payment",
    variant: "pending",
    text: "Payment required",
  },
  activating: {
    state: "activating",
    variant: "pending",
    text: "Activating your account",
  },
  // PM ruling 2026-08-31 — see the block above.
  active: { state: "active", variant: "ok", text: "Subscription active" },
  // FR47 / Story 16.6 AC10-AC11: the clock starts at calibration, so the pending
  // pill names the thing that will start it. UX §13.11 — unchanged by the ruling.
  pending_calibration: {
    state: "pending_calibration",
    variant: "pending",
    text: "Activating after calibration",
  },
  // Story 13.6 / UX §13.11 — a B2B contract lapse is a hard, settled block
  // (renewal is org-admin/CITS scope, never the reader's), which is why this row
  // alone takes the `blocked` variant.
  org_lapsed: {
    state: "org_lapsed",
    variant: "blocked",
    text: "Lapsed — read-only",
  },
  // An inactive org seat with NO passed lapse instant (contract not started /
  // NULL-dates drift) has not lapsed, and must not be labelled as though it had
  // (gh-556, 13.6 AC5).
  inactive: {
    state: "inactive",
    variant: "warning",
    text: "Subscription inactive",
  },
  // Every other inactive claim — B2C lapse, never-subscribed fallback, bare
  // pre-10.8 fixtures (Story 10.5's surface). Muted warning, never alarm-red.
  lapsed: { state: "lapsed", variant: "warning", text: "Subscription lapsed" },
};

const LEGACY_ONBOARDING_STATES: readonly RenderedPillState[] = [
  "awaiting_payment",
  "activating",
];

/**
 * The pill states that mean "billing is blocking live scoring". Deliberately
 * excludes `pending_calibration` (a CALIBRATION concern, not billing — gh-556)
 * and, of course, `active`; the legacy-onboarding rows are excluded too, since
 * those readers are gated by account activation rather than by an entitlement.
 * The workspace readiness card reads this instead of restating
 * `isSubscriptionLapsed && !isPreCalibrationSubscription`.
 */
const BILLING_BLOCKED_STATES: readonly SubscriptionPillState[] = [
  "org_lapsed",
  "inactive",
  "lapsed",
];

export function isLegacyOnboardingPillState(
  state: RenderedPillState | null,
): state is LegacyOnboardingState {
  return state !== null && LEGACY_ONBOARDING_STATES.includes(state);
}

export function isBillingBlockedPillState(
  state: SubscriptionPillState | null,
): boolean {
  return state !== null && BILLING_BLOCKED_STATES.includes(state);
}

/**
 * Which of Story 16.7's onboarding moments this clinician is in, or `null` when
 * they are in none of them.
 *
 * `isLegacyGated` is the outer gate — the JWT's own
 * `user_origin === "webapp_legacy" && !is_active`, i.e. the account has not been
 * enabled — and the billing marker splits it: a `Pre-calibration` marker means a
 * subscription row exists, so the money has arrived and only the activation
 * event has not. Anything else on the individual path (including the
 * `no_entitlement` default and an abandoned checkout, which leaves no row at all)
 * is still awaiting payment. BOTH rows require `source === "individual_subscription"`:
 * neither sentence is true of a claim the in-product checkout does not own.
 *
 * `certVerified` narrows the gate to STAGE 2, and it is load-bearing rather than
 * decorative. `isLegacyGated` spans BOTH disabled stages and both can reach a
 * RESOLVED billing read (gh-626 admits `GET /billing/subscription-state` for
 * stage-1 too). Without this clause a stage-1 clinician whose certificate is
 * still pending resolves the `no_entitlement` default and is handed an ENABLED
 * "Resume payment" button the server is guaranteed to refuse: FR39 fixes the
 * ordering, so `POST /billing/checkout-sessions/create` is admitted only when
 * `cert_verified` is true and answers LEGACY-001 to everyone else. Stage 1 is
 * Screen 14b's surface, and `isLegacyPendingVerification` below is what answers
 * for it — this selector only ever says "not mine". DW-458: `AccessReadinessCard`
 * used to omit exactly this term while `SubscriptionSection` carried it — that is
 * why the function lives here now and neither surface owns a copy of it.
 *
 * Returns `null` for an unresolved read rather than guessing: the caller's own
 * loading treatment renders first, and a wrong guess here would offer "Resume
 * payment" to somebody who has already paid.
 */
export function selectLegacyOnboardingState(input: {
  isLegacyGated: boolean;
  certVerified: boolean;
  subscription: SubscriptionState;
}): LegacyOnboardingState | null {
  if (!input.isLegacyGated) return null;
  if (!input.certVerified) return null;
  if (!isResolvedSubscription(input.subscription)) return null;
  // An ACTIVE entitlement is never an onboarding moment: a reader who reached
  // this clause holding one would be shown "Payment required", a renewal date
  // and an enabled "Resume payment" the server refuses with 409 BILL-005 — a
  // live subscriber told to buy the thing they own. The guard is cheap and it
  // stays.
  //
  // What it is NOT is a reachable state, and the reachability argument this
  // comment used to carry was simply false (PR #741 review). It claimed
  // `disable_user_access` produces it, because `auth_dependencies` exempts the
  // gated legacy cohort from the deactivation rejection. But that disable stamps
  // `blocked_at` alongside `is_active=false`, and
  // `common/legacy_carveout.is_exempt_gated_legacy` refuses the carve-out to
  // anyone carrying that stamp — so the account cannot sign in at all
  // (`UserDeactivatedException`), let alone reach Profile. Nor does anything
  // else produce it: all three entitlement providers hold an ACTIVE claim behind
  // a COMPLETED calibration (FR47 — `individual_subscription_provider`,
  // `org_seat_provider`, `manual_override_provider` each return the inactive
  // `Pre-calibration` claim until then), and calibration is precisely what a
  // gated account cannot reach (403 LEGACY-002). So this is defence against a
  // state with no known producer, which is a reason to keep the line rather than
  // to justify it with a mechanism that does not exist.
  if (input.subscription.status === "active") return null;
  // gh-641's narrowing, which `SubscriptionSection`'s sibling branch already
  // carried and this selector was extracted without: the `Pre-calibration`
  // marker is stamped by BOTH the individual-subscription provider and the
  // manual-override one (`manual_override_provider.py`), so keying on the marker
  // alone tells an override grantee "Payment received" when no payment exists.
  if (isPreCalibrationSubscription(input.subscription)) {
    // Only a real subscription may claim the money arrived. An override carrying
    // the same marker is NOT an awaiting-payment case either — it is an entitled
    // clinician waiting on calibration — so it leaves the onboarding overlay
    // entirely and falls through to the ordinary billing rows, which resolve it
    // to `pending_calibration` ("Activating after calibration"). Returning
    // "awaiting_payment" here would swap a false "Payment received" for a
    // "Resume payment" button aimed at somebody who was granted access for free.
    return input.subscription.source === "individual_subscription"
      ? "activating"
      : null;
  }
  // The SAME narrowing on the unpaid half, for the same reason (PR #741 review).
  // Both onboarding rows describe the in-product Stripe path, so both must be
  // reached only from a claim that path could own. Without this term an ORG-SEATED
  // legacy clinician — cert verified, access disabled by an admin, the
  // organization's contract lapsed — was pilled "Payment required" above
  // `OrgSeatVariant`'s body copy ending "Contact your organization administrator to
  // renew.": a pill demanding a self-serve payment the org-seat branch exists to
  // say is impossible. Anything that is not the individual path leaves the
  // onboarding overlay entirely and falls through to the ordinary billing rows,
  // which resolve it to `org_lapsed` / `inactive` — the same answer the org-seat
  // body copy beside it is already giving.
  return input.subscription.source === "individual_subscription"
    ? "awaiting_payment"
    : null;
}

export interface SubscriptionPillInput {
  subscription: SubscriptionState;
  /** JWT: legacy origin AND the account is not yet enabled. Defaults false. */
  isLegacyGated?: boolean;
  /** JWT: CITS has approved the certificate (stage 2). Defaults false. */
  certVerified?: boolean;
}

// ---------------------------------------------------------------------------
// Screen 14b — the STAGE-1 Subscription card. Client-validated, and until now
// unimplemented: a stage-1 clinician resolved the `no_entitlement` default and
// was pilled "Subscription lapsed" — the lie the table above names in so many
// words — above an ENABLED "Renew subscription" the server answers 403
// LEGACY-001.
//
// Story 16.7's W3 field contract deliberately did NOT adapt 14b's copy for its
// own rows, and its decision row 15 was right to reject it: 16.7 owns the four
// CERT-VERIFIED moments and 14b describes the one BEFORE verification. This is
// that moment, and the copy is 14b's own (mockup lines 147 and 153).
// ---------------------------------------------------------------------------

/**
 * 14b puts this sentence on BOTH of the screen's locked controls — the
 * readiness card's "Open calibration overview" and the Subscription card's
 * "Pay annual subscription". It is hoisted here for the reason
 * `ORG_LAPSE_CONTACT_ADMIN` is: two surfaces composing one sentence must not be
 * reworded on only one of them.
 */
export const LEGACY_VERIFICATION_PENDING_REASON =
  "Available after CITS verifies your certificate.";

/** 14b's `pill pending` — the pending variant, never the lapse rows' warning. */
export const LEGACY_PENDING_VERIFICATION_PILL: RenderedPillDescriptor = {
  state: "pending_verification",
  variant: "pending",
  text: "Locked until verification",
};

/**
 * Is this reader in 14b's stage-1 moment? The JWT is the outer gate —
 * `isLegacyGated` (legacy origin AND the account is not enabled) with
 * `certVerified` still false — and the billing claim narrows it the SAME way
 * both onboarding rows above are narrowed: 14b's card describes the in-product
 * Stripe checkout, so it may only speak for a claim that path could own. An org
 * seat or a manual override leaves this moment entirely and falls through to the
 * ordinary billing rows, which is the answer the body copy beside the pill is
 * already giving on those cards.
 *
 * An UNRESOLVED read answers false rather than guessing: `source` is not
 * knowable until it lands, and the surfaces render their own placeholder for
 * exactly that interval.
 */
export function isLegacyPendingVerification(
  input: SubscriptionPillInput,
): boolean {
  if (!input.isLegacyGated) return false;
  if (input.certVerified) return false;
  if (!isResolvedSubscription(input.subscription)) return false;
  return input.subscription.source === "individual_subscription";
}

/**
 * The ONE discriminator. Order matters and is the union of what the three
 * surfaces used to do separately:
 *
 *   1. a legacy-onboarding moment wins outright — the billing answer is wrong
 *      for both of them (an unpaid legacy clinician resolves the
 *      `no_entitlement` default and would be pilled "Subscription lapsed",
 *      naming a subscription they have never had);
 *   2. unresolved (`loading`, errored, malformed payload) → `null`, i.e. NO
 *      pill. Surfaces decide what an unknown state looks like — the TopBar
 *      renders nothing (no flash), the Profile card renders its placeholder;
 *   3. `status === "active"` → `active`. Equivalent to the Profile card's old
 *      `isActive` read for every resolved state (`mapSubscriptionResponse`
 *      derives one from the other) and additionally correct for the bare
 *      `{ status: "active" }` fixtures that carry no `isActive` at all;
 *   4. the FR47 paid-but-uncalibrated marker → `pending_calibration`, NEVER a
 *      lapse (gh-556);
 *   5. an org seat past a PASSED lapse instant → `org_lapsed`; an org seat with
 *      no instant → `inactive` (it has not lapsed — 13.6 AC5);
 *   6. everything else inactive → `lapsed`.
 *
 * `now` is injectable for the step-5 instant comparison only.
 */
export function selectSubscriptionPillState(
  input: SubscriptionPillInput & { subscription: ResolvedSubscriptionState },
  now?: Date,
): SubscriptionPillState;
export function selectSubscriptionPillState(
  input: SubscriptionPillInput,
  now?: Date,
): SubscriptionPillState | null;
export function selectSubscriptionPillState(
  input: SubscriptionPillInput,
  now: Date = new Date(),
): SubscriptionPillState | null {
  const { subscription } = input;

  const onboarding = selectLegacyOnboardingState({
    isLegacyGated: Boolean(input.isLegacyGated),
    certVerified: Boolean(input.certVerified),
    subscription,
  });
  if (onboarding) return onboarding;

  if (subscription.status === "loading") return null;
  if (subscription.status === "active") return "active";
  if (isPreCalibrationSubscription(subscription)) return "pending_calibration";
  if (subscription.source === "org_seat") {
    // `isSubscriptionLapseConfirmed` reduces to the passed-instant test here
    // (the pre-calibration and non-lapsed legs are already excluded above), so
    // this IS the old local `isOrgLapsed` — read through the shared predicate
    // rather than restated, which is what the ledger entry asked for.
    return isSubscriptionLapseConfirmed(subscription, now)
      ? "org_lapsed"
      : "inactive";
  }
  return "lapsed";
}

/**
 * The pill a surface renders: variant AND text, both from `SUBSCRIPTION_PILL` —
 * or, for 14b's stage-1 moment, from `LEGACY_PENDING_VERIFICATION_PILL`, which
 * is the one descriptor the table cannot hold (there is no entitlement for it to
 * describe; see `LegacyPendingVerificationState`). `null` means the state is
 * unresolved and there is no pill to render.
 *
 * Stage 1 is answered HERE rather than in `selectSubscriptionPillState` so that
 * the billing mapper keeps returning exactly the billing states — the workspace
 * card classifies every one of them through `isBillingBlockedPillState`, and a
 * moment that is not a billing block has no honest row there.
 *
 * Every subscription pill in the product goes through this one function. A
 * surface that writes its own label string — even the correct one — breaks the
 * pin test in `src/lib/__tests__/subscription-pill-single-source.test.tsx`.
 */
export function subscriptionPill(
  input: SubscriptionPillInput & { subscription: ResolvedSubscriptionState },
  now?: Date,
): RenderedPillDescriptor;
export function subscriptionPill(
  input: SubscriptionPillInput,
  now?: Date,
): RenderedPillDescriptor | null;
export function subscriptionPill(
  input: SubscriptionPillInput,
  now: Date = new Date(),
): RenderedPillDescriptor | null {
  if (isLegacyPendingVerification(input)) {
    return LEGACY_PENDING_VERIFICATION_PILL;
  }
  const state = selectSubscriptionPillState(input, now);
  return state ? SUBSCRIPTION_PILL[state] : null;
}
